import { erc20Abi, isAddress } from "viem";
import { useReadContracts } from "wagmi";
import { chainIdFromNetwork, type PaymentAccept } from "@/lib/x402";

export interface AcceptToken {
  symbol: string;
  decimals: number;
}

/**
 * Reads each payment option's symbol and decimals from its token contract.
 * The x402 requirement names only the asset address and network, so the
 * contract is the one source that holds for any chain the server offers.
 * `tokens` is undefined until every read has succeeded; `isError` is set if
 * any option's asset is not an address or any read fails.
 */
export function useAcceptTokens(accepts: PaymentAccept[]) {
  const assets = accepts.map((accept) => accept.asset);
  const allAddresses = assets.every((asset) => isAddress(asset));

  const { data, isLoading, isError } = useReadContracts({
    contracts: accepts.flatMap((accept) => {
      if (!isAddress(accept.asset)) return [];
      const target = {
        address: accept.asset,
        abi: erc20Abi,
        chainId: chainIdFromNetwork(accept.network),
      };
      return [
        { ...target, functionName: "symbol" as const },
        { ...target, functionName: "decimals" as const },
      ];
    }),
    allowFailure: false,
    query: { enabled: accepts.length > 0 && allAddresses },
  });

  let tokens: AcceptToken[] | undefined;
  let malformed = !allAddresses;
  if (data && allAddresses) {
    tokens = [];
    for (let i = 0; i < accepts.length; i++) {
      const symbol = data[i * 2];
      const decimals = data[i * 2 + 1];
      if (typeof symbol !== "string" || typeof decimals !== "number") {
        malformed = true;
        tokens = undefined;
        break;
      }
      tokens.push({ symbol, decimals });
    }
  }

  return { tokens, isLoading, isError: isError || malformed };
}
